import { useState, useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { atom_guideRoute, GuideRouteSegment, GuideStep } from '@app/shared/state/route-tracker.atom';
import { getMapRoute } from '@app/shared/services/route.service';
import { getCachedRoute, cacheRoute } from '@app/shared/services/route-cache.service';
import { Tour } from '@app/shared/models/tour.type';

interface UseMapRoutesResult {
  allRoutes: any[];
  routeGroupStopIds: string[][];
}

export function useMapRoutes(
  tourState: Tour | null | undefined,
  originalTourState: Tour | null | undefined,
  selectedFilter: string[],
): UseMapRoutesResult {
  const [allRoutes, setAllRoutes] = useState<any[]>([]);
  const [routeGroupStopIds, setRouteGroupStopIds] = useState<string[][]>([]);
  const setGuideRoute = useSetRecoilState(atom_guideRoute);

  useEffect(() => {
    fetchRoutes();
  }, [tourState, selectedFilter]);

  async function fetchRoutes() {
    const tourWaypoints_latLngReversed: {
      type: any;
      stopId: string;
      stopTitle: string;
      langAndLang: [number, number];
    }[] =
      (originalTourState?.stops || tourState?.stops)
        ?.filter((stop) => {
          const exclude = !stop?.id || stop?.s_title?.startsWith?.('Route Transition');
          const noCoords = typeof stop?.stop_location?.latitude !== 'number'
            || typeof stop?.stop_location?.longitude !== 'number';
          const excludedByPath = stop?.include_path === false;
          return !exclude && !noCoords && !excludedByPath;
        })
        ?.sort((a, b) => a?.order - b?.order)
        .map((stop) => ({
          type: stop?.direction_type,
          stopId: stop.id,
          stopTitle: stop?.s_title || stop.id,
          langAndLang: [stop?.stop_location?.longitude,
          stop?.stop_location?.latitude,] as [number, number],
        })) || [];

    for (let i = 0; i < tourWaypoints_latLngReversed.length; i++) {
      if (tourWaypoints_latLngReversed[i].type === 'car') {
        tourWaypoints_latLngReversed[i].type = 'driving-car';
      }
      else if (tourWaypoints_latLngReversed[i].type === 'none') {
        tourWaypoints_latLngReversed[i].type = 'none';
      } else if (tourWaypoints_latLngReversed[i].type === 'foot') {
        tourWaypoints_latLngReversed[i].type = 'foot-walking';
      } else if (tourWaypoints_latLngReversed[i].type === 'bike') {
        tourWaypoints_latLngReversed[i].type = 'cycling-regular';
      } else {
        tourWaypoints_latLngReversed[i].type = 'cycling-regular';
      }
    }

    if (!tourWaypoints_latLngReversed?.length) return;

    const guideStopOrder = tourWaypoints_latLngReversed.map(w => w.stopId);

    let groups = [] as { type: string, waypoints: { type: string, stopId: string, stopTitle: string, langAndLang: [number, number] }[] }[];
    let lastType = null as string | null;

    tourWaypoints_latLngReversed.forEach(waypoint => {
      if (waypoint.type !== lastType) {
        groups.push({ type: waypoint.type, waypoints: [waypoint] });
        lastType = waypoint.type;
      } else {
        groups[groups.length - 1].waypoints.push(waypoint);
      }
    });

    const routeGroups = groups.filter(group => group.type !== "none");

    // Bridge consecutive route segments: a group with only one waypoint can't be routed
    // (ORS needs 2+ points). Append the next group's first waypoint as this segment's
    // endpoint so the foot/car/bike leg actually reaches the start of the next leg.
    // Without this, removing the synthetic "Route Transition" placeholder stops would
    // leave foot=[s0], car=[s2,...] with foot un-routable.
    for (let i = 0; i < routeGroups.length - 1; i++) {
      const current = routeGroups[i];
      const next = routeGroups[i + 1];
      if (next.waypoints.length > 0) {
        current.waypoints.push(next.waypoints[0]);
      }
    }

    const tourId = tourState?.id || '';

    const allRoutes = await Promise.all(
      routeGroups.map(async (group, segmentIndex) => {
        const waypoints = group.waypoints.map(w => w.langAndLang);
        const labels = group.waypoints.map(w => `${w.stopTitle} (id=${w.stopId})`);

        const cachedRoute = await getCachedRoute(tourId, waypoints, group.type, segmentIndex);
        if (cachedRoute) {
          return cachedRoute;
        }

        try {
          const route = await getMapRoute(waypoints, group.type, labels);

          if (route && tourId) {
            await cacheRoute(tourId, waypoints, group.type, route, segmentIndex);
          }

          return route;
        } catch (error) {
          console.error(`Failed to fetch route segment ${segmentIndex}:`, error, 'stops:', labels);
          return null;
        }
      })
    );

    setAllRoutes(allRoutes);
    setRouteGroupStopIds(routeGroups.map(g => g.waypoints.map(w => w.stopId)));

    // Build GuideRoute from ORS responses for navigator tracking
    try {
      const guideSegments: GuideRouteSegment[] = [];

      routeGroups.forEach((group, groupIdx) => {
        const routeData = allRoutes[groupIdx];
        if (!routeData) return;

        const coords = routeData.features?.[0]?.geometry?.coordinates as [number, number][] | undefined;
        const wayPoints = routeData.features?.[0]?.properties?.way_points as number[] | undefined;
        const orsSegments = routeData.features?.[0]?.properties?.segments as any[] | undefined;

        if (!coords || !wayPoints || wayPoints.length < 2) return;

        const groupStopIds = group.waypoints.map(w => w.stopId);

        for (let i = 0; i < wayPoints.length - 1; i++) {
          if (i >= groupStopIds.length - 1) break;
          // Extract ORS steps for this sub-segment; resolve each step's end coord from the full polyline.
          const rawSteps = orsSegments?.[i]?.steps;
          const steps: GuideStep[] = Array.isArray(rawSteps)
            ? rawSteps.map((s: any) => {
              const endIdx = Array.isArray(s.way_points) ? s.way_points[1] : null;
              const endLngLat = (typeof endIdx === 'number' && coords[endIdx]) ? coords[endIdx] : null;
              return {
                instruction: s.instruction || '',
                distance: typeof s.distance === 'number' ? s.distance : 0,
                duration: typeof s.duration === 'number' ? s.duration : 0,
                type: typeof s.type === 'number' ? s.type : 0,
                name: s.name || '',
                endCoord: endLngLat ? { lat: endLngLat[1], lng: endLngLat[0] } : { lat: 0, lng: 0 },
              };
            })
            : [];

          guideSegments.push({
            fromStopId: groupStopIds[i],
            toStopId: groupStopIds[i + 1],
            polylineCoords: coords
              .slice(wayPoints[i], wayPoints[i + 1] + 1)
              .map(([lng, lat]) => ({ lat, lng })),
            steps,
          });
        }
      });

      if (guideStopOrder.length > 0 && guideSegments.length > 0) {
        setGuideRoute({ stopOrder: guideStopOrder, segments: guideSegments });
      }
    } catch (error) {
      console.warn('[fetchRoutes] Failed to build guide route for navigator:', error);
    }
  }

  return { allRoutes, routeGroupStopIds };
}
